import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("h3", null, "Gantt Chart", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", {
  id: "tooltip",
  style: {"display":"none","position":"absolute","top":"-5px","left":"100%","background":"white","border":"1px solid #d4d4d4","padding":"10px","z-index":"100","border-radius":"5px"}
}, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("button", { class: "dropdown-button" }, "Columns", -1)
const _hoisted_4 = { class: "dropdown-content" }

import { onMounted, onUnmounted, ref, watch, reactive } from 'vue';
import { useGanttChartStore } from '@/stores/GanttChartStore';
import 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/skins/dhtmlxgantt_material.css';
import { gantt } from 'dhtmlx-gantt';


export default /*#__PURE__*/_defineComponent({
  __name: 'GanttChartComponent',
  setup(__props) {

const ganttContainer = ref<HTMLElement | null>(null);
const store = useGanttChartStore();
const dropdownVisible = ref(false);
const visibleColumns = ref(['id', 'text', 'start_date', 'end_date', 'duration']);
const tasksForGantt = reactive({ data: [] });

function getDefaultStartDate() {
  return new Date().toISOString();
}

function getDefaultEndDate() {
  return new Date().toISOString();
}

function initGanttChart() {
  if (ganttContainer.value) {
    gantt.config.order_branch = true;
    gantt.config.autosize = "y";
    gantt.templates.task_class = function (start, end, task) {
      const taskId = String(task.id);

      if (taskId.startsWith("job-")) {
        return "job-task"; 
      } else if (taskId.startsWith("task-")) {
        return "regular-task"; 
      }
      return "";
    };
    gantt.init(ganttContainer.value);
    gantt.parse({ data: tasksForGantt.data });
    gantt.sort('id', false);
    gantt.render();
  }
}

function toggleDropdown(event) {
  event.stopPropagation();
  dropdownVisible.value = !dropdownVisible.value;
}
function closeDropdown() {
  dropdownVisible.value = false;
}
function handleClickOutside(event) {
  const dropdownElement = document.getElementById('column-visibility-dropdown');
  if (dropdownElement && !dropdownElement.contains(event.target)) {
    closeDropdown();
  }
}

watch(visibleColumns, (newColumns) => {
  gantt.config.columns = newColumns.map(column => {
    switch (column) {
      case 'id':
        return { name: "id", label: "ID", width: 100, align: "center" };
      case 'text':
        return {
          name: "text",
          label: "Task name",
          width: 200,
          tree: true,
          resize: true,
          template: function (task) {
            return `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width:200px;">${task.text}</div>`;
          }
        };
      case 'start_date':
        return { name: "start_date", label: "Start time", align: "center", width: 100 };
      case 'end_date':
        return { name: "end_date", label: "End time", align: "center", width: 100 };
      case 'duration':
        return { name: "duration", label: "Duration(Days)", align: "center", width: 100 };
      default:
        return null;
    }
  }).filter(Boolean);
  initGanttChart();
});

function updateTasksForGantt() {
  tasksForGantt.data = store.tasks.map(task => {
    const start_date = task.start_date && task.start_date !== "Not started"
      ? new Date(task.start_date)
      : new Date(getDefaultStartDate());

    const end_date = task.end_date && task.end_date !== "In progress"
      ? new Date(task.end_date)
      : new Date(getDefaultEndDate());

    return {
      ...task,
      start_date: start_date,
      end_date: end_date,
      duration: task.duration
    };
  });
}

onMounted(async () => {
  await store.fetchGanttChartTasks();
  // console.log('Tasks for Gantt Chart:', store.tasks);
  updateTasksForGantt();
  gantt.config.columns = [
    { name: "id", label: "ID", width: 100, align: "center" },
    {
      name: "text",
      label: "Task name",
      width: 200,
      tree: true,
      template: function (task) {
        return `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width:200px;">${task.text}</div>`;
      }
    },
    { name: "start_date", label: "Start date", align: "center", width: 100 },
    { name: "end_date", label: "End date", align: "center", width: 100 },
    { name: "duration", label: "Duration(Days)", width: 100, align: "center" }
  ];
  gantt.templates.task_text = function (start, end, task) {
    return `#${task.id} ${task.text}`;
  };

  initGanttChart();

  const tooltip = document.getElementById('tooltip');
  gantt.attachEvent("onMouseMove", function (id, e) {
    if (!id) {
      tooltip.style.display = "none";
      return;
    }
    const task = gantt.getTask(id);
    const startDate = task.start_date ? new Date(task.start_date).toLocaleDateString() : 'Not set';
    const endDate = task.end_date ? new Date(task.end_date).toLocaleDateString() : 'Not set';
    const duration = task.duration ? task.duration + ' days' : 'Not set';

    tooltip.innerHTML = `
    <strong>Task:</strong> ${task.text}<br>
    <strong>Start Date:</strong> ${startDate}<br>
    <strong>End Date:</strong> ${endDate}<br>
    <strong>Duration:</strong> ${duration}
  `;
    tooltip.style.display = "block";
    tooltip.style.top = e.clientY + 15 + 'px';
    tooltip.style.left = e.clientX + 5 + 'px';
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _hoisted_1,
    _hoisted_2,
    _createElementVNode("div", {
      id: "column-visibility-dropdown",
      class: "dropdown",
      onClick: toggleDropdown
    }, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("label", null, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            value: "id",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((visibleColumns).value = $event))
          }, null, 512), [
            [_vModelCheckbox, visibleColumns.value]
          ]),
          _createTextVNode(" ID")
        ]),
        _createElementVNode("label", null, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            value: "text",
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((visibleColumns).value = $event))
          }, null, 512), [
            [_vModelCheckbox, visibleColumns.value]
          ]),
          _createTextVNode(" Task Name")
        ]),
        _createElementVNode("label", null, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            value: "start_date",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((visibleColumns).value = $event))
          }, null, 512), [
            [_vModelCheckbox, visibleColumns.value]
          ]),
          _createTextVNode(" Start Date")
        ]),
        _createElementVNode("label", null, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            value: "end_date",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((visibleColumns).value = $event))
          }, null, 512), [
            [_vModelCheckbox, visibleColumns.value]
          ]),
          _createTextVNode(" End Date")
        ]),
        _createElementVNode("label", null, [
          _withDirectives(_createElementVNode("input", {
            type: "checkbox",
            value: "duration",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((visibleColumns).value = $event))
          }, null, 512), [
            [_vModelCheckbox, visibleColumns.value]
          ]),
          _createTextVNode(" Duration")
        ])
      ])
    ]),
    _createElementVNode("div", {
      ref_key: "ganttContainer",
      ref: ganttContainer,
      id: "gantt_here",
      class: "gantt_container--material",
      style: {"width":"100%","height":"780px"}
    }, null, 512)
  ], 64))
}
}

})